/**
 * The feature tables: what each tier includes, what each locked feature needs,
 * and what each tier costs. Data only — no imports, by design.
 *
 * They lived in `core/composables/useFeatureGating.js` until Phase 1F, which is
 * fine for Vue and fatal for the state layer: `core/state/features.ts` needs
 * them, and reaching into that composable dragged `@/core/store` — the Pinia
 * mirrors — back into `core/state/ui.ts`, closing a cycle. Every module in it
 * then evaluated to `undefined` and three suites failed on
 * "Cannot read properties of undefined (reading 'getState')".
 *
 * `useFeatureGating.js` re-exports all three, so nothing that imported them
 * from there has to change.
 */

export const TIER_FEATURES: Record<string, Record<string, boolean>> = {
  // Demo tier - view-only experience
  demo: {
    view_map: true,
    view_scenarios: true,
    view_data: true,
    view_reports: true,
    run_optimization: false,
    run_simulation: false,
    edit_data: false,
    import_data: false,
    export_data: false,
    save_scenarios: false,
    multi_period: false,
    uncertainty: false,
    monte_carlo: false,
    ai_chat: false,
    api_access: false
  },
  
  // Trial tier mirrors enterprise capabilities during evaluation
  trial: {
    view_map: true,
    view_scenarios: true,
    view_data: true,
    view_reports: true,
    run_optimization: true,
    edit_data: true,
    import_data: true,
    export_data: true,
    save_scenarios: true,
    multi_period: true,
    run_simulation: true,
    uncertainty: true,
    monte_carlo: true,
    ai_chat: true,
    api_access: true
  },
  
  // Essential tier - $2,000/month
  essential: {
    export_powerpoint: true,   // feature_gates: essential and above
    view_map: true,
    view_scenarios: true,
    view_data: true,
    view_reports: true,
    run_optimization: true,
    edit_data: true,
    import_data: true,
    export_data: true,
    save_scenarios: true,
    run_simulation: false,
    multi_period: false,
    uncertainty: false,
    monte_carlo: false,
    ai_chat: false,
    api_access: false
  },
  
  // Professional tier - $4,000/month
  professional: {
    export_powerpoint: true,   // feature_gates: essential and above
    view_map: true,
    view_scenarios: true,
    view_data: true,
    view_reports: true,
    run_optimization: true,
    edit_data: true,
    import_data: true,
    export_data: true,
    save_scenarios: true,
    multi_period: true,
    run_simulation: false,
    uncertainty: false,
    monte_carlo: false,
    ai_chat: false,
    api_access: false
  },
  
  // Enterprise tier - $6,000/month
  enterprise: {
    export_powerpoint: true,   // feature_gates: essential and above
    view_map: true,
    view_scenarios: true,
    view_data: true,
    view_reports: true,
    run_optimization: true,
    edit_data: true,
    import_data: true,
    export_data: true,
    save_scenarios: true,
    multi_period: true,
    run_simulation: true,
    uncertainty: true,
    monte_carlo: true,
    ai_chat: true,
    api_access: true
  }
}


export const FEATURE_REQUIREMENTS: Record<string, { minTier: string; displayName: string; description: string }> = {
  run_optimization: {
    minTier: 'essential',
    displayName: 'Run Optimization',
    description: 'Run custom scenario optimizations'
  },
  run_simulation: {
    minTier: 'enterprise',
    displayName: 'Run Simulation',
    description: 'Monte Carlo simulation with uncertainty modeling'
  },
  multi_period: {
    minTier: 'professional',
    displayName: 'Multi-Period Planning',
    description: 'Model seasonal demand and capacity over time'
  },
  uncertainty: {
    minTier: 'enterprise',
    displayName: 'Uncertainty Modeling',
    description: 'Model demand, lead time, and fill rate uncertainty'
  },
  monte_carlo: {
    minTier: 'enterprise',
    displayName: 'Monte Carlo Analysis',
    description: 'Run hundreds of simulations to test risk'
  },
  ai_chat: {
    minTier: 'enterprise',
    displayName: 'AI Chat Advisor',
    description: 'Ask questions and get instant insights'
  },
  edit_data: {
    minTier: 'essential',
    displayName: 'Edit Data',
    description: 'Modify facilities, lanes, and demand'
  },
  import_data: {
    minTier: 'essential',
    displayName: 'Import Data',
    description: 'Upload Excel files with your network data'
  },
  export_data: {
    minTier: 'essential',
    displayName: 'Export Data',
    description: 'Download Excel reports and presentations'
  }
}


export const TIER_PRICING: Record<string, Record<string, unknown>> = {
  demo: {
    name: 'Demo',
    price: 0,
    period: 'Free',
    color: '#10b981'
  },
  trial: {
    name: 'Trial',
    price: 0,
    period: '14 days',
    color: '#2563eb'
  },
  essential: {
    name: 'Essential',
    price: 2000,
    period: 'month',
    color: '#0066cc'
  },
  professional: {
    name: 'Professional',
    price: 4000,
    period: 'month',
    color: '#0066cc'
  },
  enterprise: {
    name: 'Enterprise',
    price: 6000,
    period: 'month',
    color: '#0066cc'
  }
}

